import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function ContactPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contact';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact BrightSync | Data Center Critical Parts Readiness",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contact`,
      "description": "Contact BrightSync for critical parts readiness coordination for data center operations in Austin and Dallas-Fort Worth, Texas.",
      "inLanguage": "en-US",
      "isPartOf": {
        "@type": "WebSite",
        "name": "BrightSync",
        "url": import.meta.env.VITE_SITE_URL || "https://example.com"
      },
      "mainEntity": {
        "@type": "Organization",
        "name": "BrightSync",
        "email": "arunk@bright-sync.com",
        "url": import.meta.env.VITE_SITE_URL || "https://example.com",
        "areaServed": [
          { "@type": "City", "name": "Austin" },
          { "@type": "City", "name": "Dallas-Fort Worth" }
        ],
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "TX",
          "addressCountry": "US"
        }
      }
    });
    if (!document.getElementById('schema-contact')) {
      document.head.appendChild(script);
    }
    return () => {
      const existing = document.getElementById('schema-contact');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/3bf68d6c09283a962d249fcc9ea91526/ab1e605b7823c60e0472793f0d68ad5d.png"
                alt="BrightSync Logo"
                className="h-16 w-auto"
              />
              <span className="text-3xl font-bold text-[#1A2332]">BrightSync</span>
            </Link>
            <Link
              to="/"
              className="text-[#5A6670] hover:text-[#0A5F5C] transition-colors duration-300 font-medium flex items-center space-x-2 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Contact Hero */}
      <section className="py-24 lg:py-32 bg-[#1A2332] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#0A5F5C] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#0A5F5C] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <div className="w-16 h-16 flex items-center justify-center bg-[#0A5F5C]/20 rounded-2xl mx-auto mb-8">
            <i className="ri-mail-send-line text-3xl text-[#0A5F5C]"></i>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Get in Touch
          </h1>
          <p className="text-lg text-[#B8C5D0] max-w-2xl mx-auto mb-12">
            Have questions about BrightSync&apos;s critical parts readiness coordination? We&apos;d love to hear from you. Reach out to us directly.
          </p>
        </div>
      </section>

      {/* Contact Details */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Email Card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow duration-300 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-[#0A5F5C]/10 rounded-xl mx-auto mb-6">
                <i className="ri-mail-line text-2xl text-[#0A5F5C]"></i>
              </div>
              <h3 className="text-lg font-bold text-[#1A2332] mb-3">Email Us</h3>
              <a
                href="mailto:arunk@bright-sync.com"
                className="text-[#0A5F5C] hover:text-[#084A48] font-medium transition-colors cursor-pointer break-all"
              >
                arunk@bright-sync.com
              </a>
            </div>

            {/* Location Card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow duration-300 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-[#0A5F5C]/10 rounded-xl mx-auto mb-6">
                <i className="ri-map-pin-line text-2xl text-[#0A5F5C]"></i>
              </div>
              <h3 className="text-lg font-bold text-[#1A2332] mb-3">Service Area</h3>
              <p className="text-[#5A6670] text-sm leading-relaxed">
                Austin &amp; Dallas-Fort Worth, TX<br />
                Texas Infrastructure Corridors
              </p>
            </div>

            {/* Response Time Card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow duration-300 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-[#0A5F5C]/10 rounded-xl mx-auto mb-6">
                <i className="ri-time-line text-2xl text-[#0A5F5C]"></i>
              </div>
              <h3 className="text-lg font-bold text-[#1A2332] mb-3">Response Time</h3>
              <p className="text-[#5A6670] text-sm leading-relaxed">
                We typically respond within<br />
                <strong className="text-[#1A2332]">24 hours</strong>
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <div className="bg-[#F8F7F5] rounded-3xl p-10 lg:p-14">
              <h2 className="text-2xl lg:text-3xl font-bold text-[#1A2332] mb-4">
                Ready to Discuss Your Needs?
              </h2>
              <p className="text-[#5A6670] mb-8 max-w-xl mx-auto">
                Drop us an email and let&apos;s explore how BrightSync can help reduce critical component uncertainty for your data center operations.
              </p>
              <a
                href="mailto:arunk@bright-sync.com"
                className="inline-flex items-center space-x-2 bg-[#0A5F5C] text-white px-8 py-4 rounded-lg hover:bg-[#084A48] transition-all duration-300 font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                <span>arunk@bright-sync.com</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F8F7F5] pt-8 pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="border-t border-gray-300 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-[#5A6670] text-sm">
              © 2025 BrightSync. All rights reserved.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-[#5A6670] hover:text-[#0A5F5C] transition-colors text-sm whitespace-nowrap"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
